import { Component, OnChanges, OnDestroy, SimpleChanges, Input, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { ApexOptions } from 'apexcharts';

import { ChartType } from '../apextypes';
import { IDataPointSelectedArguments } from './interfaces';
import ApexCharts from 'apexcharts';

@Component({
    selector: 'azavista3-apexcharts-wrapper',
    templateUrl: 'apexcharts-wrapper.component.html',
    standalone: false
})
export class ApexchartsWrapperComponent implements OnChanges, OnDestroy {
    @Input() type: ChartType;
    @Input() series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    @Input() labels: string[];
    @Input() width: number;
    @Input() height: number;

    @Output() dataPointSelected = new EventEmitter<IDataPointSelectedArguments>();

    apexCharts: ApexCharts;

    @ViewChild('apexchartsContainer', { static: true }) private apexchartsContainer: ElementRef<HTMLElement>;
    private apexOptions: ApexOptions;
    private readonly maxLabelLength = 40;

    constructor() {
        this.apexOptions = {
            series: [],
            labels: [],
            chart: {
                events: {
                    dataPointSelection: (e: MouseEvent, chart?: Record<string, unknown>, options?: { dataPointIndex: number, seriesIndex: number }) =>
                        this.processChartDataPointSelection(options)
                }
            }
        };
    }

    private processChartDataPointSelection(options?: { dataPointIndex: number, seriesIndex: number }): void {
        if (!options) {
            return;
        }
        this.dataPointSelected.emit({ dataPointIndex: options.dataPointIndex, seriesIndex: options.seriesIndex });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        let sizeChanged = false;
        let chartTypeChanged = false;
        if (changes.type) {
            this.apexOptions.chart.type = this.type;
            await this.createChartsInstance();
            chartTypeChanged = true;
        }

        const chart: { width?: number, height?: number } = {};
        if (changes.width && changes.width.currentValue) {
            chart.width = this.width;
            if (!changes.height) {
                this.height = this.adjustHeight(this.type, this.width, this.height);
                chart.height = this.height;
            }
            sizeChanged = true;
        }

        if (chartTypeChanged || (changes.height && changes.height.currentValue)) {
            this.height = this.adjustHeight(this.type, this.width, this.height);
            chart.height = this.height;
            sizeChanged = true;
        }

        if (sizeChanged) {
            await this.apexCharts.updateOptions({ chart: chart });
        }

        // It seems that if series and labels are changed, first the labels must be changed and then series updated
        // othewise the series change animation does not take place
        if (chartTypeChanged || changes.labels) {
            const labels = this.adjustLabels(this.labels);
            this.apexCharts.updateOptions({ labels: labels });
        }

        if (chartTypeChanged || changes.series) {
            await this.apexCharts.updateSeries(this.series);
        }

    }

    /**
     *
     * @param labels Truncates labels to 40 characters max
     */
    adjustLabels(labels: string[]): string[] {
        if (!labels) {
            return labels;
        }

        const adjustedLabels = labels.map(x => {
            if (x?.length > this.maxLabelLength) {
                return x.substring(0, this.maxLabelLength - 3) + '...';
            } else {
                return x;
            }
        });
        return adjustedLabels;
    }

    adjustHeight(chartType: ChartType, chartWidth: number, desiredHeight: number): number {
        // Setting height is a little bit more complicated
        // On square type of charts (donut), available height should not be more than the width
        let newHeight: number;
        if (chartType === 'donut') {
            newHeight = (chartWidth && desiredHeight > chartWidth) ? chartWidth : desiredHeight;
            const minDiff = 80;
            if (chartWidth && (chartWidth - newHeight) < minDiff) {
                newHeight = chartWidth - minDiff;
            }
        } else {
            newHeight = desiredHeight;
        }
        // The diff between width-height should not be less than 50
        return newHeight;
    }

    ngOnDestroy(): void {
        this.destroyChart();
    }

    async createChartsInstance(): Promise<void> {
        this.destroyChart();
        this.apexOptions.chart.toolbar = this.apexOptions.chart.toolbar || { show: false };
        this.apexOptions.chart.zoom = this.apexOptions.chart.zoom || { enabled: false };
        this.apexCharts = new ApexCharts(this.apexchartsContainer.nativeElement, this.apexOptions);
        await this.apexCharts.render();
    }

    destroyChart(): void {
        if (this.apexCharts) {
            this.apexCharts.destroy();
        }
    }
}
